import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Heart, MessageCircle, Send, ArrowLeft, Wifi, WifiOff, Coins, CreditCard, Image as ImageIcon, X, Loader2, MapPin } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNotifications } from '../../context/NotificationContext';
import API_URL from '../../utils/apiUrl';
import { t } from '../../i18n/t';

// Talk sites are chat sites: no dating heart.
const isTalk = process.env.REACT_APP_MODE === 'talk';
const BrandMark = isTalk ? MessageCircle : Heart;

const WS_URL = (typeof window !== 'undefined')
  ? `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`
  : API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const DatingChat = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { user, getAuthHeader, token } = useAuth();
  const { clearUnread, setCurrentChatUser } = useNotifications();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [credits, setCredits] = useState(0);
  const [imageAttachment, setImageAttachment] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  // REF: Always holds the CURRENT chat partner ID.
  // This prevents the stale-closure bug where switching chats
  // causes old WebSocket handlers to add messages to the wrong conversation.
  const currentChatRef = useRef(userId);

  // Fetch credits balance
  const fetchCredits = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/credits/balance`, {
        headers: getAuthHeader()
      });
      setCredits(response.data.credits || 0);
    } catch (error) {
      console.error('Failed to fetch credits:', error);
    }
  }, [getAuthHeader]);

  // Keep the ref in sync with the URL param
  useEffect(() => {
    currentChatRef.current = userId;
  }, [userId]);

  // WebSocket connection — does NOT depend on userId (stable across chat switches)
  const connectWebSocket = useCallback(() => {
    if (!user?.id) return;
    
    try {
      const ws = new WebSocket(`${WS_URL}/ws/chat/${user.id}?token=${token}`);
      
      ws.onopen = () => {
        console.log('WebSocket connected');
        setWsConnected(true);
      };
      
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        // Read the CURRENT chat partner from the ref, not from the closure
        const activeChatId = currentChatRef.current;
        
        if (data.type === 'new_message') {
          const msg = data.message;
          
          if (activeChatId && (msg.sender_id === activeChatId || msg.receiver_id === activeChatId)) {
            setMessages(prev => {
              const exists = prev.some(m => m.id === msg.id);
              if (!exists) return [...prev, msg];
              return prev;
            });
          }

          // Clear typing indicator when message arrives
          if (msg.sender_id === activeChatId) {
            setTypingUser(null);
          }
          
          fetchInbox();
          
          if (msg.sender_id !== activeChatId && msg.sender_id !== user?.id) {
            toast.info(`${t('newMessageFrom')} ${msg.sender_username || t('someone')}`);
          }
        }
        
        if (data.type === 'message_sent') {
          const msg = data.message;
          // Only add to current chat if the message belongs to this conversation
          if (activeChatId && (msg.sender_id === activeChatId || msg.receiver_id === activeChatId)) {
            setMessages(prev => {
              const exists = prev.some(m => m.id === msg.id);
              if (!exists) {
                return [...prev, msg];
              }
              return prev;
            });
          }
          
          // Update credits from response
          if (data.remaining_credits !== undefined) {
            setCredits(data.remaining_credits);
          }
        }

        if (data.type === 'error' && data.code === 'INSUFFICIENT_CREDITS') {
          toast.error(data.message, {
            action: {
              label: t('buyCredits'),
              onClick: () => navigate('/credits')
            }
          });
        }

        if (data.type === 'typing_indicator') {
          if (data.is_typing && data.sender_id === activeChatId) {
            setTypingUser(data.sender_username || t('someone'));
            if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = setTimeout(() => setTypingUser(null), 30000);
          } else if (!data.is_typing && data.sender_id === activeChatId) {
            setTypingUser(null);
            if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
          }
        }
      };
      
      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setWsConnected(false);
        
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, 3000);
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };
      
      wsRef.current = ws;
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      setWsConnected(false);
    }
  }, [user?.id, navigate]);

  useEffect(() => {
    connectWebSocket();
    fetchCredits();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [connectWebSocket, fetchCredits]);

  useEffect(() => {
    fetchInbox();
    clearUnread();
  }, []);

  useEffect(() => {
    if (userId) {
      // Clear stale state from previous chat immediately
      setTypingUser(null);
      setMessages([]);
      fetchConversation(userId);
      fetchUserDetails(userId);
      setCurrentChatUser(userId);
      // Mark messages as read
      axios.post(`${API_URL}/api/messages/read/${userId}`, {}, { headers: getAuthHeader() }).catch(() => {});
      clearUnread();
    }
    return () => setCurrentChatUser(null);
  }, [userId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fallback polling
  useEffect(() => {
    if (!wsConnected && userId) {
      const interval = setInterval(() => {
        fetchConversation(userId);
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [wsConnected, userId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchInbox = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/messages/inbox`, {
        headers: getAuthHeader()
      });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to fetch inbox:', error);
    }
    setLoading(false);
  };

  const fetchConversation = async (otherId) => {
    try {
      const response = await axios.get(`${API_URL}/api/messages/conversation/${otherId}`, {
        headers: getAuthHeader()
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to fetch conversation:', error);
    }
  };

  const fetchUserDetails = async (otherId) => {
    try {
      const response = await axios.get(`${API_URL}/api/profiles/${otherId}`, {
        headers: getAuthHeader()
      });
      setSelectedUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user details:', error);
    }
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error(t('onlyImagesAllowed'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('imageUnder5mb'));
      return;
    }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API_URL}/api/upload/image`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      setImageAttachment({ url: response.data.url, name: file.name });
      toast.success(t('imageReady'));
    } catch (error) {
      toast.error(t('failedUploadImage'));
    }
    setUploadingImage(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !imageAttachment) || !userId) return;

    // Check credits first
    if (credits < 1) {
      toast.error(t('needCredits'), {
        action: {
          label: t('buyCredits'),
          onClick: () => navigate('/credits')
        }
      });
      return;
    }

    setSendingMessage(true);
    const attachmentUrl = imageAttachment?.url || null;
    
    // Try WebSocket first
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'message',
        receiver_id: userId,
        content: newMessage || (attachmentUrl ? t('sentImage') : ''),
        image_attachment_url: attachmentUrl
      }));
      setNewMessage('');
      setImageAttachment(null);
      setSendingMessage(false);
    } else {
      // Fallback to HTTP
      try {
        const response = await axios.post(`${API_URL}/api/messages`, {
          receiver_id: userId,
          content: newMessage || (attachmentUrl ? t('sentImage') : ''),
          image_attachment_url: attachmentUrl
        }, { headers: getAuthHeader() });

        setMessages([...messages, response.data]);
        setNewMessage('');
        setImageAttachment(null);
        fetchInbox();
        
        // Update credits
        if (response.data.remaining_credits !== undefined) {
          setCredits(response.data.remaining_credits);
        } else {
          fetchCredits();
        }
      } catch (error) {
        if (error.response?.status === 402) {
          toast.error(t('insufficientCredits'), {
            action: {
              label: t('buyCredits'),
              onClick: () => navigate('/credits')
            }
          });
        } else {
          toast.error(t('failedSendMessage'));
        }
      }
      setSendingMessage(false);
    }
  };

  const selectConversation = (otherId) => {
    navigate(`/chat/${otherId}`);
  };

  const defaultAvatar = 'https://images.unsplash.com/photo-1593704345154-791a1430ac8f?w=100&h=100&fit=crop';

  return (
    <div className="h-screen flex flex-col dating-bg font-dating-body">
      {/* Header */}
      <header className="bg-white border-b border-rose-100 px-4 py-3 flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/discover')}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-2 flex-1">
          <BrandMark className="h-6 w-6 text-rose-500 fill-rose-500" />
          <span className="font-dating-heading text-lg font-bold text-slate-800">{t('messages')}</span>
        </div>
        
        {/* Credits Display */}
        {isTalk ? (
          <Button
            onClick={() => navigate('/credits')}
            className="msg-cta group h-10 rounded-full px-3 sm:px-5 font-semibold hover:bg-transparent"
            data-testid="buy-messages-btn"
          >
            <Coins className="h-4 w-4 sm:mr-2 transition-transform group-hover:rotate-12" />
            <span className="hidden sm:inline">{t('buyMessagesHere')}</span>
            <span className="ml-2 rounded-full bg-white/25 px-2 py-0.5 text-xs" data-testid="header-credits">
              {credits}
            </span>
          </Button>
        ) : (
        <button
          onClick={() => navigate('/credits')}
          className="flex items-center gap-2 px-3 py-1.5 bg-rose-50 rounded-full hover:bg-rose-100 transition-colors"
          data-testid="credits-btn"
        >
          <Coins className="h-4 w-4 text-rose-500" />
          <span className="font-semibold text-rose-700">{credits}</span>
          {credits < 5 && (
            <span className="text-xs text-rose-500">{t('low')}</span>
          )}
        </button>
        )}

        {/* Connection Status */}
        <div className={`flex items-center gap-1 text-xs ${wsConnected ? 'text-green-500' : 'text-slate-400'}`}>
          {wsConnected ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Conversations List */}
        <div className={`w-full md:w-80 border-r border-rose-100 bg-white overflow-hidden ${userId ? 'hidden md:block' : ''}`}>
          <ScrollArea className="h-full">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-rose-500"></div>
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-8 px-4">
                <p className="text-slate-500">{t('noConversations')}</p>
                <Button
                  variant="link"
                  className="text-rose-500 mt-2"
                  onClick={() => navigate('/discover')}
                >
                  {t('browseProfiles')}
                </Button>
              </div>
            ) : (
              conversations.map((conv) => (
                <div
                  key={conv.other_user_id}
                  className={`flex items-center gap-3 p-4 cursor-pointer border-b border-rose-50 transition-colors hover:bg-rose-50 ${
                    userId === conv.other_user_id ? 'bg-rose-50' : ''
                  }`}
                  onClick={() => selectConversation(conv.other_user_id)}
                  data-testid={`conv-${conv.other_user_id}`}
                >
                  <Avatar>
                    <AvatarImage src={(() => { const av = conv.other_user?.main_avatar || defaultAvatar; return av.startsWith('http') ? av : `${API_URL}${av}`; })()} />
                    <AvatarFallback>{conv.other_user?.username?.[0] || '?'}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-800">
                        {conv.other_user?.username || t('userFallback')}
                      </span>
                      {conv.unread_count > 0 && (
                        <span className="bg-rose-500 text-white text-xs px-2 py-0.5 rounded-full">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 truncate">
                      {conv.last_message?.content || t('noMessages')}
                    </p>
                  </div>
                </div>
              ))
            )}
          </ScrollArea>
        </div>

        {/* Chat Area */}
        {userId ? (
          <div className="flex-1 flex flex-col bg-slate-50">
            {/* Chat Header */}
            {selectedUser && (
              <div className="bg-white border-b border-rose-100 px-4 py-3 flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden"
                  onClick={() => navigate('/chat')}
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <Avatar
                  className="cursor-pointer"
                  onClick={() => navigate(`/profile/${selectedUser.id}`)}
                >
                  <AvatarImage src={(() => { const av = selectedUser.main_avatar || defaultAvatar; return av.startsWith('http') ? av : `${API_URL}${av}`; })()} />
                  <AvatarFallback>{selectedUser.username?.[0] || '?'}</AvatarFallback>
                </Avatar>
                <div>
                  <div className="font-medium text-slate-800">{selectedUser.username}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1">
                    {selectedUser.age ? <span>{selectedUser.age} {t('yearsOldShort')}</span> : null}
                    {selectedUser.is_online && (
                      <span className="flex items-center gap-1 text-emerald-600 font-medium" data-testid="chat-partner-online">
                        <span className="live-dot h-1.5 w-1.5 rounded-full bg-emerald-500" /> {t('online')}
                      </span>
                    )}
                    {selectedUser.display_city && (
                      <span className="flex items-center gap-0.5" data-testid="chat-partner-city">
                        · <MapPin className="h-3 w-3" /> {selectedUser.display_city}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={msg.sender_id === user?.id ? 'chat-bubble-sent' : 'chat-bubble-received'}
                      data-testid={`msg-${msg.id}`}
                    >
                      {msg.content}
                      {msg.image_attachment_url && (
                        <img
                          src={msg.image_attachment_url.startsWith('http') ? msg.image_attachment_url : `${API_URL}${msg.image_attachment_url}`}
                          alt="attachment"
                          className="mt-2 rounded-lg max-w-xs"
                        />
                      )}
                      {msg.image_url && (
                        <img
                          src={msg.image_url.startsWith('http') ? msg.image_url : `${API_URL}${msg.image_url}`}
                          alt="photo"
                          className="mt-2 rounded-lg max-w-xs"
                        />
                      )}
                    </div>
                  </div>
                ))}
                {typingUser && (
                  <div className="flex justify-start" data-testid="typing-indicator">
                    <div className="chat-bubble-received flex items-center gap-2 text-slate-500 italic text-sm">
                      <span>{typingUser} is writing</span>
                      <span className="flex gap-0.5">
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                      </span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Message Input */}
            <form onSubmit={sendMessage} className="bg-white border-t border-rose-100 p-4">
              {credits < 1 && (
                <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Coins className="h-5 w-5 text-amber-500" />
                    <span className="text-sm text-amber-700">{t('needCredits')}</span>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => navigate('/credits')}
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                  >
                    <CreditCard className="h-4 w-4 mr-1" />
                    {t('buyCredits')}
                  </Button>
                </div>
              )}
              {imageAttachment && (
                <div className="mb-2 flex items-center gap-2 p-2 bg-rose-50 rounded-lg" data-testid="image-preview">
                  <img src={imageAttachment.url} alt="attachment" className="h-12 w-12 object-cover rounded" />
                  <span className="text-sm text-slate-600 flex-1 truncate">{imageAttachment.name}</span>
                  <Button type="button" variant="ghost" size="icon" onClick={() => setImageAttachment(null)} data-testid="remove-image-btn">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleImageSelect}
                  data-testid="image-file-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={credits < 1 || uploadingImage}
                  onClick={() => fileInputRef.current?.click()}
                  className="text-rose-400 hover:text-rose-600 hover:bg-rose-50"
                  data-testid="attach-image-btn"
                >
                  {uploadingImage ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImageIcon className="h-5 w-5" />}
                </Button>
                <Input
                  type="text"
                  placeholder={credits < 1 ? t('buyCreditsToSend') : t('typeMessage')}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  className="flex-1"
                  disabled={credits < 1}
                  data-testid="message-input"
                />
                <Button
                  type="submit"
                  className="bg-rose-500 hover:bg-rose-600 text-white"
                  disabled={sendingMessage || (!newMessage.trim() && !imageAttachment) || credits < 1}
                  data-testid="send-btn"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </form>
          </div>
        ) : (
          <div className="hidden md:flex flex-1 items-center justify-center bg-slate-50">
            <div className="text-center">
              <BrandMark className="h-16 w-16 text-rose-200 mx-auto mb-4" />
              <p className="text-slate-500">{t('selectConversation')}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DatingChat;
